import React, { Component } from 'react';
import { connect } from 'react-redux';
import { update } from 'ducks/app';
import PropTypes from 'prop-types';
import Qs from 'qs';
import Url from 'url';
import classNames from 'classnames';

import { claim } from '../../../../ducks/achievements';
import { isMobile, openLogin } from '../../../../mobile/helpers';
import Flash from '../Flash';
import FloatingButton from './components/FloatingButton';
import styles from './Scanner.scss';
import withUser from '../../../../components/withUser';

/* global QRScanner */
/* eslint-disable no-underscore-dangle */

const propTypes = {
  dispatch: PropTypes.func,
  user: PropTypes.shape({}),
  children: PropTypes.node,
  transparent: PropTypes.bool,
};

export const ScannerContext = React.createContext();

class Scanner extends Component {
  constructor(props) {
    super(props);

    this.state = {
      err: undefined,
      status: {},
      loading: false,
      sharedContext: {
        activateScanner: this.onActivate,
      },
    };
  }

  componentDidMount() {
    if (typeof QRScanner !== 'undefined') {
      QRScanner.getStatus((currently) => {
        this.updateStatus('componentDidMount')(currently);
      });
    } else {
      window.c = (a, e) => this.props.dispatch(claim(a, e));
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(update({ transparent: false }));

    if (typeof QRScanner !== 'undefined') {
      QRScanner.destroy();
    }
  }

  onScan = (err, data) => {
    if (data) {
      const { query } = Url.parse(data);
      const { a, e } = Qs.parse(query);
      const { dispatch } = this.props;
      dispatch(claim(a, e));
      this.onDeactivate();
    }
  }

  onDeactivate = () => {
    const { dispatch } = this.props;
    dispatch(update({ transparent: false }));
    QRScanner.destroy(this.updateStatus('onDeactivate'));
  }

  startScanning = () => {
    QRScanner.scan(this.onScan);
    QRScanner.show((status) => {
      const { dispatch } = this.props;
      this.updateStatus('startScanning')(status);
      dispatch(update({ transparent: true }));
      this.setState({ loading: false });
    });
  }

  onActivate = () => {
    this.setState({ loading: true, err: null });
    QRScanner.getStatus((currently) => {
      if (!currently.authorized) {
        QRScanner.prepare((err, status) => {
          if (status && status.authorized && !status.scanning) {
            this.startScanning();
          } else {
            this.updateStatus('onActivate')(status, err);
          }
        });
      } else {
        this.startScanning();
      }
    });
  }

  updateStatus = (why) => (status, err) => {
    const { dispatch } = this.props;

    // Debug camera status
    /* eslint-disable-next-line no-console */
    console.log(
      `New camera status (${why})`,
      status && Object.entries(status)
        .filter((a) => a[1])
        .map((a) => a[0])
        .join(', '),
      err && `ERR!: ${err._message}`,
    );

    // If we get an error, don't set status since it's null/undef.
    // Always set err though, since err == null/undef is no error.
    if (status) {
      // Special case for android when opening the camera the second time it
      // does not always run the QRScanner.show callback to turn the page transparent
      if (status.scanning && !err && why !== 'onDeactivate') {
        dispatch(update({ transparent: true }));
      }

      this.setState({ status, err });
    } else {
      this.setState({ err });
    }
  }

  onSwitchCamera = () => {
    const { status } = this.state;
    if (status.currentCamera === 1) {
      QRScanner.useBackCamera((err, s) => this.updateStatus('onSwitchCamera')(s, err));
    } else {
      QRScanner.useFrontCamera((err, s) => this.updateStatus('onSwitchCamera')(s, err));
    }
  }

  onSwitchLight = () => {
    const { status } = this.state;
    if (status.lightEnabled) {
      QRScanner.disableLight((err, s) => this.updateStatus('onSwitchLight')(s, err));
    } else {
      QRScanner.enableLight((err, s) => this.updateStatus('onSwitchLight')(s, err));
    }
  }

  render() {
    const { status, err, loading } = this.state;
    const { user, transparent } = this.props;

    const sharedContext = {
      ...this.state.sharedContext,
      available: isMobile() && user && !transparent,
      loading: loading && !err,
    };

    if (!isMobile()) {
      return (
        <ScannerContext.Provider value={sharedContext}>
          {this.props.children}
          <div className={styles.base}>
            <FloatingButton
              onClick={() => window.open('https://dreamhack.com/app/download')}
              content="Get the app to scan codes"
              icon="far fa-cloud-download"
            />
          </div>
        </ScannerContext.Provider>
      );
    }

    if (!user) {
      return (
        <ScannerContext.Provider value={sharedContext}>
          {this.props.children}
          <div className={styles.base}>
            <FloatingButton
              content="Log in to scan codes"
              icon="far fa-sign-in"
              onClick={() => openLogin()}
            />
          </div>
        </ScannerContext.Provider>
      );
    }

    return (
      <ScannerContext.Provider value={sharedContext}>
        {this.props.children}
        <Flash
          title="Whoops!"
          message={err ? err._message : undefined}
          error
        />
        <Flash
          title="Whoops!"
          message={status && status.restricted && 'There seems to be parental controls or organizational security restrictions stopping camera access.'}
          error
        />
        <div className={styles.base}>
          {!transparent && (
            <>
              {status.denied && (
                <FloatingButton
                  onClick={() => QRScanner.openSettings()}
                  content="Scan code"
                />
              )}
              {!status.authorized && !status.denied && (
                <FloatingButton
                  onClick={() => this.onActivate()}
                  content="Scan code"
                />
              )}
              {status.authorized && (
                <FloatingButton
                  onClick={() => this.onActivate()}
                  content="Scan code"
                />
              )}
            </>
          )}
          {transparent && (
            <>
              <div
                className={classNames(
                  styles.cancel,
                )}
                onClick={this.onDeactivate}
                role="button"
                tabIndex="-1"
              >
                <i className="far fa-times" />
              </div>
              {status.canChangeCamera && (
                <div
                  className={classNames(
                    styles.changeCamera,
                  )}
                  onClick={this.onSwitchCamera}
                  role="button"
                  tabIndex="-1"
                >
                  <i className="far fa-repeat" />
                </div>
              )}

              {status.canEnableLight && (
                <div
                  className={classNames(
                    styles.toggleFlash,
                  )}
                  onClick={this.onSwitchLight}
                  role="button"
                  tabIndex="-1"
                >
                  {!status.lightEnabled && (
                    <i className="far fa-lightbulb" />
                  )}
                  {status.lightEnabled && (
                    <i className="far fa-lightbulb-slash" />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </ScannerContext.Provider>
    );
  }
}

Scanner.propTypes = propTypes;

const mapStateToProps = (state) => ({
  transparent: state.getIn(['app', 'transparent']),
});

export default withUser(connect(mapStateToProps)(Scanner));
